// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  GridGroupNode,
  gridFilteredSortedRowIdsSelector,
  gridRowTreeSelector,
  useGridApiContext,
} from '@mui/x-data-grid-premium';
import { useEffect, useState } from 'react';

/**
 * A React hook that manages the expansion state of rows in a Material UI
 * DataGrid component.
 */
export function useDataGridRowExpansion() {
  const apiRef = useGridApiContext();

  // Unfortunately Material UI does not provide an observable way to check if
  // any rows are currently expanded. Keeping state in sync with the grid is
  // a workaround but certainly less than ideal.
  const [rowExpansion, setRowExpansion] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const handleRowExpansionChange = (node: GridGroupNode) => {
      setRowExpansion((rowExpansion) => ({
        ...rowExpansion,
        [node.id]: node.childrenExpanded ?? false,
      }));
    };

    const unsubscribe = apiRef.current.subscribeEvent(
      'rowExpansionChange',
      handleRowExpansionChange
    );

    return unsubscribe;
  }, [apiRef]);

  const isExpanded = Object.values(rowExpansion).some(Boolean);

  const getGroupIds = () => {
    const tree = gridRowTreeSelector(apiRef);
    const rows = gridFilteredSortedRowIdsSelector(apiRef);

    return rows.filter((rowId) => tree[rowId].type === 'group');
  };

  const toggle = (isExpanded: boolean) => {
    const groupIds = getGroupIds();

    groupIds.forEach((rowId) => {
      apiRef.current.setRowChildrenExpansion(rowId, isExpanded);
    });
  };

  const expand = () => {
    toggle(true);
  };

  const collapse = () => {
    toggle(false);
  };

  return { isExpanded, toggle, expand, collapse };
}
